/**
 * ApplyPreviewTree — the dated preview of a standard-apply as an Antd tree-table
 * (native `children` rows built from `preview.tasks` via parentKey, same idea as
 * the SP1 editor). Leaves carry an inline-editable qty (→ qtyOverrides → re-preview)
 * with a provenance tag; summaries show em-dashes. A prune checkbox on any row
 * removes the row (a summary prunes its whole subtree). Per-task warnings surface
 * as a tooltip icon.
 * Visual register: Sovereign Emerald.
 */
import { Checkbox, InputNumber, Table, Tag, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { TriangleAlert } from 'lucide-react';
import { useMemo } from 'react';
import { SOVEREIGN } from '../../../utils/epcProjectVisual';
import type { ApplyPreviewTask, QtyProvenance } from '../../types';

const PROVENANCE_TAG: Record<QtyProvenance, { color: string; label: string }> = {
    norm: { color: 'green', label: 'norm' },
    adjusted: { color: 'gold', label: 'adjusted' },
    overridden: { color: 'geekblue', label: 'overridden' },
};

export interface ApplyPreviewTreeProps {
    tasks: ApplyPreviewTask[];
    /** keys currently pruned (checkbox off). */
    prunedKeys: Set<string>;
    onTogglePrune: (task: ApplyPreviewTask, pruned: boolean) => void;
    /** leaf qty edited inline → sets a per-task override (undefined clears it). */
    onQtyOverride: (taskKey: string, qty: number | undefined) => void;
}

interface Row {
    rowKey: string;
    task: ApplyPreviewTask;
    isLeaf: boolean;
    children?: Row[];
}

/** parentKey tree over the pre-ordered preview tasks; leaves omit `children`. */
function buildRows(tasks: ApplyPreviewTask[]): Row[] {
    const byParent = new Map<string | null, ApplyPreviewTask[]>();
    for (const t of tasks) {
        byParent.set(t.parentKey, [...(byParent.get(t.parentKey) ?? []), t]);
    }
    const toRows = (parentKey: string | null): Row[] =>
        (byParent.get(parentKey) ?? []).map((t) => {
            const kids = toRows(t.key);
            return {
                rowKey: t.key,
                task: t,
                isLeaf: kids.length === 0 && !t.isSummary,
                ...(kids.length > 0 ? { children: kids } : {}),
            };
        });
    return toRows(null);
}

export function ApplyPreviewTree({
    tasks,
    prunedKeys,
    onTogglePrune,
    onQtyOverride,
}: ApplyPreviewTreeProps) {
    const rows = useMemo(() => buildRows(tasks), [tasks]);
    const expandedKeys = useMemo(
        () => tasks.filter((t) => t.isSummary || t.parentKey === null).map((t) => t.key),
        [tasks],
    );

    const columns = useMemo<ColumnsType<Row>>(
        () => [
            {
                title: 'Task',
                key: 'task',
                render: (_: unknown, row: Row) => (
                    <span className="inline-flex min-w-0 flex-col">
                        <span
                            className="truncate text-sm"
                            style={{
                                color: SOVEREIGN.ink,
                                fontWeight: row.isLeaf ? 500 : 700,
                            }}
                        >
                            {row.task.name}
                            {row.task.milestone && (
                                <Tooltip title="Milestone">
                                    <span
                                        aria-label="Milestone"
                                        className="ml-1.5"
                                        style={{ color: SOVEREIGN.champagne }}
                                    >
                                        ◆
                                    </span>
                                </Tooltip>
                            )}
                            {row.task.warnings.length > 0 && (
                                <Tooltip
                                    title={
                                        <ul className="m-0 list-disc pl-4">
                                            {row.task.warnings.map((w) => (
                                                <li key={w}>{w}</li>
                                            ))}
                                        </ul>
                                    }
                                >
                                    <TriangleAlert
                                        size={12}
                                        className="ml-1.5 inline align-[-1px]"
                                        style={{ color: '#C08A0A' }}
                                        aria-label={`${row.task.warnings.length} warnings`}
                                    />
                                </Tooltip>
                            )}
                        </span>
                        <span
                            className="truncate font-mono text-[10.5px]"
                            style={{ color: SOVEREIGN.kpiCaption }}
                        >
                            {row.task.wbs} · {row.task.key}
                        </span>
                    </span>
                ),
            },
            {
                title: 'Discipline',
                key: 'discipline',
                width: 120,
                render: (_: unknown, row: Row) => (
                    <span
                        className="text-sm"
                        style={{
                            color: row.task.disciplineKey ? SOVEREIGN.ink : SOVEREIGN.kpiCaption,
                        }}
                    >
                        {row.task.disciplineKey ?? '—'}
                    </span>
                ),
            },
            {
                title: 'Qty',
                key: 'qty',
                width: 190,
                render: (_: unknown, row: Row) => {
                    const q = row.task.qty;
                    if (!row.isLeaf || !q) {
                        return (
                            <span className="text-sm" style={{ color: SOVEREIGN.kpiCaption }}>
                                —
                            </span>
                        );
                    }
                    const prov = PROVENANCE_TAG[q.provenance];
                    return (
                        <span className="inline-flex items-center gap-1.5">
                            <InputNumber
                                aria-label={`Quantity for ${row.task.key}`}
                                size="small"
                                min={0.001}
                                value={q.value}
                                onChange={(v) =>
                                    onQtyOverride(
                                        row.task.key,
                                        typeof v === 'number' && v > 0 ? v : undefined,
                                    )
                                }
                                style={{
                                    width: 84,
                                    background: SOVEREIGN.inputBg,
                                    borderColor: SOVEREIGN.inputBorder,
                                }}
                            />
                            <span
                                className="text-[11px] font-semibold"
                                style={{ color: SOVEREIGN.bottle }}
                            >
                                {q.unit}
                            </span>
                            <Tag
                                color={prov.color}
                                style={{ marginInlineEnd: 0, fontSize: 10, lineHeight: '16px' }}
                            >
                                {prov.label}
                            </Tag>
                        </span>
                    );
                },
            },
            {
                title: 'Duration',
                key: 'duration',
                width: 92,
                align: 'right',
                render: (_: unknown, row: Row) =>
                    row.task.isSummary ? (
                        <span className="text-sm" style={{ color: SOVEREIGN.kpiCaption }}>
                            —
                        </span>
                    ) : (
                        <span
                            className="text-sm tabular-nums"
                            style={{ color: SOVEREIGN.ink }}
                        >
                            {row.task.durationDays} wd
                        </span>
                    ),
            },
            {
                title: 'Dates',
                key: 'dates',
                width: 190,
                render: (_: unknown, row: Row) => (
                    <span className="text-[12px] tabular-nums" style={{ color: SOVEREIGN.ink }}>
                        {row.task.start} → {row.task.finish}
                    </span>
                ),
            },
            {
                title: 'Include',
                key: 'prune',
                width: 72,
                align: 'center',
                render: (_: unknown, row: Row) => (
                    <Tooltip
                        title={
                            row.task.isSummary
                                ? 'Unchecking prunes this branch and its subtree'
                                : 'Uncheck to exclude this task'
                        }
                    >
                        <Checkbox
                            checked={!prunedKeys.has(row.task.key)}
                            aria-label={`Include ${row.task.key}`}
                            onChange={(e) => onTogglePrune(row.task, !e.target.checked)}
                        />
                    </Tooltip>
                ),
            },
        ],
        [prunedKeys, onTogglePrune, onQtyOverride],
    );

    return (
        <Table<Row>
            columns={columns}
            dataSource={rows}
            rowKey={(r) => r.rowKey}
            size="small"
            pagination={false}
            defaultExpandedRowKeys={expandedKeys}
            scroll={{ y: 380 }}
            locale={{ emptyText: 'No tasks in the resolved standard.' }}
        />
    );
}

export default ApplyPreviewTree;
